/**
 * FASE 0: DIAGNÓSTICO DE GEOCODIFICACIÓN PTEL
 * 
 * Script para identificar puntos exactos de fallo en el flujo de geocodificación.
 * 
 * USO:
 * 1. Importar en DevTools console: import('./utils/geocodingDiagnostic').then(d => d.runDiagnostic())
 * 2. O usar desde componente: import { runDiagnostic } from '../utils/geocodingDiagnostic'
 * 
 * FECHA: 06-Ene-2026
 * FASE: 0 - Diagnóstico
 */

import { loadLocalData, isDataLoaded, searchLocal, getLoadStats as _getLoadStats } from '../lib/LocalDataService';
import { deconcatenateText } from '../lib/textDeconcatenator';
import { isDatabaseReady } from '../lib/localData/schemas';
import { GeocodingOrchestrator } from '../services/geocoding/GeocodingOrchestrator';

// ============================================================================
// TIPOS
// ============================================================================

interface DiagnosticResult {
  timestamp: string;
  tests: DiagnosticTest[];
  summary: {
    passed: number;
    failed: number;
    warnings: number;
  };
  recommendations: string[];
}

interface DiagnosticTest {
  name: string;
  status: 'PASS' | 'FAIL' | 'WARN';
  details: string;
  data?: unknown;
  duration?: number;
}

// ============================================================================
// T0.1: VERIFICAR CARGA DE DATOS DERA
// ============================================================================

async function testLocalDataLoading(): Promise<DiagnosticTest> {
  const start = performance.now();
  console.log('[DIAG] T0.1: Verificando carga de datos DERA...');
  
  try {
    // Verificar si ya están cargados
    const wasLoaded = isDataLoaded();
    console.log(`[DIAG] isDataLoaded() inicial: ${wasLoaded}`);
    
    // Forzar carga si no estaban
    const stats = await loadLocalData();
    const duration = Math.round(performance.now() - start);
    
    console.log('[DIAG] Stats de carga:', stats);
    
    // Verificar cantidad esperada (~11,276 features)
    const expectedMinimum = 10000;
    if (stats.totalFeatures >= expectedMinimum) {
      return {
        name: 'T0.1 - Carga datos DERA',
        status: 'PASS',
        details: `${stats.totalFeatures} features cargados de ${stats.municipiosIndexados} municipios`,
        data: stats,
        duration
      };
    } else {
      return {
        name: 'T0.1 - Carga datos DERA',
        status: 'WARN',
        details: `Solo ${stats.totalFeatures} features (esperado >${expectedMinimum})`,
        data: stats,
        duration
      };
    }
  } catch (error) {
    return {
      name: 'T0.1 - Carga datos DERA',
      status: 'FAIL',
      details: `Error: ${error instanceof Error ? error.message : String(error)}`,
      duration: Math.round(performance.now() - start)
    };
  }
}

// ============================================================================
// T0.2: VERIFICAR IndexedDB (SingletonDetector)
// ============================================================================

async function testIndexedDB(): Promise<DiagnosticTest> {
  const start = performance.now();
  console.log('[DIAG] T0.2: Verificando IndexedDB para SingletonDetector...');
  
  try {
    const ready = await isDatabaseReady();
    const duration = Math.round(performance.now() - start);
    
    if (ready) {
      return {
        name: 'T0.2 - IndexedDB SingletonDetector',
        status: 'PASS',
        details: 'IndexedDB lista para consultas',
        duration
      };
    } else {
      return {
        name: 'T0.2 - IndexedDB SingletonDetector',
        status: 'WARN',
        details: 'IndexedDB no lista - fallback a LocalDataService',
        duration
      };
    }
  } catch (error) {
    return {
      name: 'T0.2 - IndexedDB SingletonDetector',
      status: 'FAIL',
      details: `Error: ${error instanceof Error ? error.message : String(error)}`,
      duration: Math.round(performance.now() - start)
    };
  }
}

// ============================================================================
// T0.3: PROBAR DESCONCATENACIÓN
// ============================================================================

function testDeconcatenation(): DiagnosticTest {
  console.log('[DIAG] T0.3: Probando desconcatenación...');
  
  const testCases = [
    // Casos reales de documentos PTEL
    { input: 'FARMACIAM.ªCarmenMAYOL', expected: 'FARMACIA M.ª Carmen MAYOL' },
    { input: 'CENTROSALUD', expected: 'CENTRO SALUD' },
    { input: 'CONSULTORIOMEDICO', expected: 'CONSULTORIO MEDICO' },
    { input: 'GuardiacivilColomera', expected: 'Guardia civil Colomera' },
    { input: 'Sierrade Castril', expected: 'Sierra de Castril' },
    // Casos que NO deben modificarse
    { input: 'Centro de Salud', expected: 'Centro de Salud' },
    { input: 'Farmacia López', expected: 'Farmacia López' },
  ];
  
  const results: { input: string; output: string; expected: string; match: boolean }[] = [];
  let passed = 0;
  let failed = 0;
  
  for (const tc of testCases) {
    const result = deconcatenateText(tc.input);
    const match = result.corrected === tc.expected;
    results.push({
      input: tc.input,
      output: result.corrected,
      expected: tc.expected,
      match
    });
    
    if (match) passed++;
    else failed++;
    
    console.log(
      `[DIAG] ${match ? '✅' : '❌'} "${tc.input}" → "${result.corrected}" ` +
      `(esperado: "${tc.expected}")`
    );
  }
  
  if (failed === 0) {
    return {
      name: 'T0.3 - Desconcatenación',
      status: 'PASS',
      details: `${passed}/${testCases.length} casos correctos`,
      data: results
    };
  } else {
    return {
      name: 'T0.3 - Desconcatenación',
      status: 'WARN',
      details: `${passed}/${testCases.length} correctos, ${failed} fallidos`,
      data: results
    };
  }
}

// ============================================================================
// T0.4: PROBAR BÚSQUEDA LOCAL
// ============================================================================

async function testLocalSearch(): Promise<DiagnosticTest> {
  const start = performance.now();
  console.log('[DIAG] T0.4: Probando búsqueda local DERA...');
  
  // Casos de prueba con infraestructuras conocidas
  const testCases = [
    { nombre: 'Centro de Salud', municipio: 'Colomera', codMun: '18051' },
    { nombre: 'Colegio', municipio: 'Colomera', codMun: '18051' },
    { nombre: 'Ayuntamiento', municipio: 'Guadix', codMun: '18089' },
    { nombre: 'Guardia Civil', municipio: 'Quéntar', codMun: '18165' },
  ];
  
  const results: { query: string; found: boolean; matchScore: number; matchedName?: string }[] = [];
  let found = 0;
  
  for (const tc of testCases) {
    try {
      const result = await searchLocal({
        nombre: tc.nombre,
        codMun: tc.codMun,
        municipio: tc.municipio,
        threshold: 0.4
      });
      
      results.push({
        query: `${tc.nombre} (${tc.municipio})`,
        found: result.success,
        matchScore: result.matchScore,
        matchedName: result.bestMatch?.nombre
      });
      
      if (result.success) {
        found++;
        console.log(
          `[DIAG] ✅ "${tc.nombre}" → "${result.bestMatch?.nombre}" ` +
          `(score: ${result.matchScore}%, ${result.searchTime}ms)`
        );
      } else {
        console.log(
          `[DIAG] ❌ "${tc.nombre}" no encontrado en ${tc.municipio} ` +
          `(${result.totalSearched} registros buscados)`
        );
      }
    } catch (error) {
      results.push({
        query: `${tc.nombre} (${tc.municipio})`,
        found: false,
        matchScore: 0
      });
      console.log(`[DIAG] ❌ Error buscando "${tc.nombre}": ${error}`);
    }
  }
  
  const duration = Math.round(performance.now() - start);
  
  if (found === testCases.length) {
    return {
      name: 'T0.4 - Búsqueda local DERA',
      status: 'PASS',
      details: `${found}/${testCases.length} infraestructuras encontradas`,
      data: results,
      duration
    };
  } else if (found > 0) {
    return {
      name: 'T0.4 - Búsqueda local DERA',
      status: 'WARN',
      details: `${found}/${testCases.length} encontradas`,
      data: results,
      duration
    };
  } else {
    return {
      name: 'T0.4 - Búsqueda local DERA',
      status: 'FAIL',
      details: 'Ninguna infraestructura encontrada en datos locales',
      data: results,
      duration
    };
  }
}

// ============================================================================
// T0.5: PROBAR GEOCODIFICACIÓN COMPLETA
// ============================================================================

async function testGeocodingOrchestrator(): Promise<DiagnosticTest> {
  const start = performance.now();
  console.log('[DIAG] T0.5: Probando GeocodingOrchestrator...');
  
  const orchestrator = new GeocodingOrchestrator();
  
  // Precargar datos
  console.log('[DIAG] Precargando datos locales...');
  await orchestrator.preloadLocalData();
  
  // Casos de prueba
  const testCases = [
    {
      name: 'Centro de Salud',
      municipality: 'Colomera',
      province: 'Granada',
      codMun: '18051'
    },
    {
      name: 'Ayuntamiento de Guadix',
      municipality: 'Guadix',
      province: 'Granada',
      codMun: '18089'
    },
    {
      name: 'CEIP San Miguel',
      municipality: 'Colomera',
      province: 'Granada',
      codMun: '18051'
    }
  ];
  
  const results: {
    name: string;
    success: boolean;
    geocoder: string;
    confidence?: number;
    attempts: string[];
    errors: string[];
  }[] = [];
  
  let succeeded = 0;
  
  for (const tc of testCases) {
    console.log(`[DIAG] Geocodificando: "${tc.name}" (${tc.municipality})...`);
    
    const result = await orchestrator.geocode({
      name: tc.name,
      municipality: tc.municipality,
      province: tc.province,
      codMun: tc.codMun,
      useLocalData: true,
      useGenericFallback: true,
      crossValidate: false // Desactivar para diagnóstico más rápido
    });
    
    const success = result.geocoding !== null;
    
    results.push({
      name: tc.name,
      success,
      geocoder: result.geocoderUsed,
      confidence: result.geocoding?.confidence,
      attempts: result.attempts,
      errors: result.errors
    });
    
    if (success) {
      succeeded++;
      console.log(
        `[DIAG] ✅ "${tc.name}" → ${result.geocoderUsed} ` +
        `(confianza: ${result.geocoding?.confidence}%, ${result.processingTime}ms)`
      );
    } else {
      console.log(
        `[DIAG] ❌ "${tc.name}" no geocodificado\n` +
        `    Intentos: ${result.attempts.join(' → ')}\n` +
        `    Errores: ${result.errors.join('; ') || 'ninguno'}`
      );
    }
  }
  
  const duration = Math.round(performance.now() - start);
  
  if (succeeded === testCases.length) {
    return {
      name: 'T0.5 - GeocodingOrchestrator',
      status: 'PASS',
      details: `${succeeded}/${testCases.length} geocodificados correctamente`,
      data: results,
      duration
    };
  } else if (succeeded > 0) {
    return {
      name: 'T0.5 - GeocodingOrchestrator',
      status: 'WARN',
      details: `${succeeded}/${testCases.length} geocodificados`,
      data: results,
      duration
    };
  } else {
    return {
      name: 'T0.5 - GeocodingOrchestrator',
      status: 'FAIL',
      details: 'Ninguna infraestructura geocodificada',
      data: results,
      duration
    };
  }
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

export async function runDiagnostic(): Promise<DiagnosticResult> {
  console.log('╔══════════════════════════════════════════════════════════════╗');
  console.log('║     FASE 0: DIAGNÓSTICO DE GEOCODIFICACIÓN PTEL             ║');
  console.log('║                   06-Ene-2026                                ║');
  console.log('╚══════════════════════════════════════════════════════════════╝');
  console.log('');
  
  const tests: DiagnosticTest[] = [];
  
  // Ejecutar tests en orden
  tests.push(await testLocalDataLoading());
  tests.push(await testIndexedDB());
  tests.push(testDeconcatenation());
  tests.push(await testLocalSearch());
  tests.push(await testGeocodingOrchestrator());
  
  // Calcular resumen
  const summary = {
    passed: tests.filter(t => t.status === 'PASS').length,
    failed: tests.filter(t => t.status === 'FAIL').length,
    warnings: tests.filter(t => t.status === 'WARN').length
  };
  
  // Generar recomendaciones
  const recommendations: string[] = [];
  
  for (const test of tests) {
    if (test.status === 'FAIL') {
      if (test.name.includes('DERA')) {
        recommendations.push(
          'Los datos DERA no se cargan. Verificar:\n' +
          '  - Archivos en /public/data/dera/*.geojson existen\n' +
          '  - La app tiene acceso a /data/dera/* vía fetch\n' +
          '  - Revisar consola por errores CORS o 404'
        );
      }
      if (test.name.includes('IndexedDB')) {
        recommendations.push(
          'IndexedDB no está lista. Verificar:\n' +
          '  - El navegador soporta IndexedDB\n' +
          '  - No hay errores de cuota de almacenamiento\n' +
          '  - El esquema se inicializa correctamente'
        );
      }
      if (test.name.includes('Orchestrator')) {
        recommendations.push(
          'El orquestador falla. Verificar:\n' +
          '  - Los niveles de cascada se ejecutan en orden\n' +
          '  - No hay errores de red en L5-L7\n' +
          '  - La clasificación tipológica es correcta'
        );
      }
    }
  }
  
  if (summary.failed === 0 && summary.warnings === 0) {
    recommendations.push(
      '✅ Todos los tests pasaron. Si la geocodificación sigue fallando,\n' +
      '   el problema puede estar en:\n' +
      '   - La extracción de datos del documento (documentExtractor)\n' +
      '   - El flujo entre Step1 y Step2 del procesamiento\n' +
      '   - Nombres de infraestructuras muy diferentes a los datos DERA'
    );
  }
  
  // Imprimir resumen
  console.log('');
  console.log('═══════════════════════════════════════════════════════════════');
  console.log('                        RESUMEN                                 ');
  console.log('═══════════════════════════════════════════════════════════════');
  console.log(`  ✅ PASS: ${summary.passed}`);
  console.log(`  ⚠️  WARN: ${summary.warnings}`);
  console.log(`  ❌ FAIL: ${summary.failed}`);
  console.log('');
  
  if (recommendations.length > 0) {
    console.log('RECOMENDACIONES:');
    recommendations.forEach((r, i) => console.log(`${i + 1}. ${r}`));
  }
  
  console.log('');
  console.log('═══════════════════════════════════════════════════════════════');
  
  const result: DiagnosticResult = {
    timestamp: new Date().toISOString(),
    tests,
    summary,
    recommendations
  };
  
  // También exportar a window para acceso fácil
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).lastDiagnostic = result;
    console.log('Resultado guardado en window.lastDiagnostic');
  }
  
  return result;
}

// ============================================================================
// HELPERS PARA USO EN CONSOLA
// ============================================================================

/**
 * Test rápido de datos DERA
 * Uso: import('./utils/geocodingDiagnostic').then(d => d.quickTestDERA())
 */
export async function quickTestDERA(): Promise<void> {
  console.log('[QuickTest] Verificando datos DERA...');
  const stats = await loadLocalData();
  console.log(`[QuickTest] ✅ ${stats.totalFeatures} features de ${stats.municipiosIndexados} municipios`);
  console.table(stats.byCategory);
}

/**
 * Test rápido de geocodificación
 * Uso: import('./utils/geocodingDiagnostic').then(d => d.quickGeocode('Centro de Salud', 'Colomera'))
 */
export async function quickGeocode(nombre: string, municipio: string): Promise<void> {
  console.log(`[QuickGeocode] Buscando "${nombre}" en ${municipio}...`);
  
  const orchestrator = new GeocodingOrchestrator();
  await orchestrator.preloadLocalData();
  
  const result = await orchestrator.geocode({
    name: nombre,
    municipality: municipio,
    province: 'Granada',
    useLocalData: true,
    useGenericFallback: true,
    crossValidate: false
  });
  
  if (result.geocoding) {
    console.log(`[QuickGeocode] ✅ Encontrado vía ${result.geocoderUsed}`);
    console.log(`  Coordenadas: X=${result.geocoding.x}, Y=${result.geocoding.y}`);
    console.log(`  Confianza: ${result.geocoding.confidence}%`);
    console.log(`  Match: "${result.geocoding.matchedName}"`);
  } else {
    console.log(`[QuickGeocode] ❌ No encontrado`);
    console.log(`  Intentos: ${result.attempts.join(' → ')}`);
    console.log(`  Errores: ${result.errors.join('; ') || 'ninguno'}`);
  }
}

export default {
  runDiagnostic,
  quickTestDERA,
  quickGeocode
};
